// src/lib/queryKeys.ts
/**
 * Structured query key factory for TanStack Query.
 *
 * Every Supabase data read in the app is identified by a structured query key.
 * This enables:
 *   - Deduplication: two components requesting the same data share one request
 *   - Targeted invalidation: realtime event → invalidate only the affected query
 *   - Background refetching: stale data refreshes without loading spinners
 *
 * Convention: [domain, scope, ...params]
 *   - queryKeys.reservations.all           → ['reservations']
 *   - queryKeys.reservations.list(filters) → ['reservations', 'list', filters]
 *   - queryKeys.reservations.detail(id)    → ['reservations', 'detail', id]
 *
 * Invalidating a parent key refetches all children:
 *   queryClient.invalidateQueries({ queryKey: ['reservations'] })
 */

export const queryKeys = {
  // ── HR / Time Tracking ──────────────────────────────────────────
  hr: {
    all: ['hr'] as const,
    state: (userId: string, date: string) => ['hr', 'state', userId, date] as const,
    // Tenant-wide active clock-in states for the Team-on-Duty widget. Under
    // the ['hr', ...] prefix so realtimeStore's hr_state handler (which
    // invalidates the whole 'hr' branch, see TABLE_QUERY_MAP) refetches the
    // widget live — the ad-hoc key ['hr','active_states',...] previously sat
    // OUTSIDE any invalidation path and went stale (owner ticket 2026-10-13:
    // widget "funktioniert nicht zuverlässig, mal besser mal schlechter").
    activeStates: (tenantId: string, date: string) => ['hr', 'activeStates', tenantId, date] as const,
    timeLogs: (userId: string) => ['hr', 'timeLogs', userId] as const,
    timeLogsRange: (userId: string, dateFrom: string, dateTo: string) => ['hr', 'timeLogs', userId, dateFrom, dateTo] as const,
    requests: (userId: string) => ['hr', 'requests', userId] as const,
    teamRequests: (tenantId: string) => ['hr', 'teamRequests', tenantId] as const,
    shifts: (tenantId: string) => ['hr', 'shifts', tenantId] as const,
    shiftComments: (tenantId: string) => ['hr', 'shiftComments', tenantId] as const,
  },

  // ── Shift Handover (Schichtübergabe) ────────────────────────────
  handover: {
    all: ['handover'] as const,
    // location segment: '' = all locations; a property id scopes the cache so
    // per-location fetches never cross-contaminate (same pattern as the
    // traces server-filters queryKey, TracesInvisibleForNonAdminRoles).
    list: (tenantId: string, location: string = '') =>
      ['handover', 'list', tenantId, location] as const,
    items: (handoverId: string) => ['handover', 'items', handoverId] as const,
  },

  // ── Users ───────────────────────────────────────────────────────
  users: {
    all: ['users'] as const,
    list: (tenantId: string) => ['users', 'list', tenantId] as const,
    detail: (tenantId: string, userId: string) => ['users', 'detail', tenantId, userId] as const,
  },

  // ── Tenants ─────────────────────────────────────────────────────
  tenants: {
    all: ['tenants'] as const,
    detail: (tenantId: string) => ['tenants', 'detail', tenantId] as const,
  },

  // ── Reservations ────────────────────────────────────────────────
  reservations: {
    all: ['reservations'] as const,
    list: (filters: Record<string, unknown>) => ['reservations', 'list', filters] as const,
    detail: (id: string) => ['reservations', 'detail', id] as const,
  },

  // ── Guests ──────────────────────────────────────────────────────
  guests: {
    all: ['guests'] as const,
    list: (tenantId: string) => ['guests', 'list', tenantId] as const,
    search: (tenantId: string, term: string) => ['guests', 'search', tenantId, term] as const,
    detail: (guestId: string) => ['guests', 'detail', guestId] as const,
  },

  // ── Global Search / Explorer ────────────────────────────────────
  search: {
    all: ['search'] as const,
    global: (tenantId: string, term: string) => ['search', 'global', tenantId, term] as const,
    // Per-type "load more" query (ExplorerResultGroup expansion)
    globalMore: (tenantId: string, term: string, entityType: string) =>
      ['search', 'globalMore', tenantId, term, entityType] as const,
    // Semantic (vector) matches via /api/search-semantic (mistral-embed)
    semantic: (tenantId: string, term: string) => ['search', 'semantic', tenantId, term] as const,
  },

  // ── Settings ────────────────────────────────────────────────────
  // The settings table stores multiple config rows per tenant (hr_settings,
  // kb_settings, bs_settings, etc.). Use byId() to avoid query conflicts.
  settings: {
    all: ['settings'] as const,
    tenant: (tenantId: string) => ['settings', 'tenant', tenantId] as const,
    byId: (tenantId: string, settingId: string) => ['settings', 'tenant', tenantId, settingId] as const,
  },

  // ── Traces ──────────────────────────────────────────────────────
  traces: {
    all: ['traces'] as const,
    list: (tenantId: string) => ['traces', 'list', tenantId] as const,
    detail: (traceId: string) => ['traces', 'detail', traceId] as const,
    activities: (traceId: string) => ['traces', 'activities', traceId] as const,
    backups: (tenantId: string) => ['traces', 'backups', tenantId] as const,
    // Traces grouped by their linked Wartungsfenster (maintenance window).
    // Lives UNDER the ['traces'] prefix so the existing realtime invalidation
    // (realtimeStore traces → ['traces']) refreshes it automatically — the P.13
    // "silently stale view" class (inline key outside every invalidation
    // prefix, Team-on-Duty incident 2026-10-13).
    maintenanceLinks: (tenantId: string) =>
      ['traces', 'maintenanceLinks', tenantId] as const,
  },

  // ── Company Contracts ───────────────────────────────────────────
  contracts: {
    all: ['contracts'] as const,
    list: (tenantId: string) => ['contracts', 'list', tenantId] as const,
    detail: (contractId: string) => ['contracts', 'detail', contractId] as const,
    ratePlanLinks: (contractId: string) => ['contracts', 'ratePlanLinks', contractId] as const,
    files: (contractId: string) => ['contracts', 'files', contractId] as const,
    auditLog: (contractId: string) => ['contracts', 'auditLog', contractId] as const,
  },

  // ── Offers ──────────────────────────────────────────────────────
  offers: {
    all: ['offers'] as const,
    list: (tenantId: string) => ['offers', 'list', tenantId] as const,
    detail: (offerId: string) => ['offers', 'detail', offerId] as const,
  },

 // ── BusinessClub ────────────────────────────────────────────────
 businessClub: {
   all: ['businessClub'] as const,
   members: (tenantId: string) => ['businessClub', 'members', tenantId] as const,
   config: (tenantId: string) => ['businessClub', 'config', tenantId] as const,
   // Rolling per-member stats window: 'YYYY-MM-DD' date range over Apaleo
   // reservations (aggregated per member email / company name client-side).
   stats: (from: string, to: string) => ['businessClub', 'stats', from, to] as const,
 },

 // ── Sales CRM (pipeline) ────────────────────────────────────────
 salesCrm: {
   all: ['salesCrm'] as const,
   companies: (tenantId: string) => ['salesCrm', 'companies', tenantId] as const,
   activities: (tenantId: string, companyId?: string | null) =>
     companyId
       ? (['salesCrm', 'activities', tenantId, companyId] as const)
       : (['salesCrm', 'activities', tenantId] as const),
 },

  // ── KPI Snapshots (analytics KPI-Entwicklungen) ─────────────────
  kpiSnapshots: {
    all: ['kpiSnapshots'] as const,
    // Scope = 'all' (every property in one fetch) or a specific property id.
    // Range = [from, to] date strings; the range slice keeps the cache precise.
    // Kind = 'actual' (daily actuals) or 'forecast' (on-the-books pickup rows).
    range: (tenantId: string, from: string, to: string, scope: string, kind = 'actual') =>
      ['kpiSnapshots', 'range', tenantId, from, to, scope, kind] as const,
    // Pickup evolution: forecast rows for ONE business date across as_of dates.
    pickup: (tenantId: string, businessDate: string, scope: string) =>
      ['kpiSnapshots', 'pickup', tenantId, businessDate, scope] as const,
    // Monthly pickup evolution (kpi_monthly_pickup RPC): one row per capture
    // day for a month window.
    monthlyPickup: (tenantId: string, scope: string, month: string) =>
      ['kpiSnapshots', 'monthlyPickup', tenantId, scope, month] as const,
  },

  // ── KPI Budgets (monthly net-revenue budget per property) ───────
  kpiBudgets: {
    all: ['kpiBudgets'] as const,
    year: (tenantId: string, year: string) => ['kpiBudgets', 'year', tenantId, year] as const,
  },

  // ── KPI Property Config (fixed physical room counts) ────────────
  // Denominator for the owner's "Occ % ohne OOO" formula.
  kpiPropertyConfig: {
    all: ['kpiPropertyConfig'] as const,
    list: (tenantId: string) => ['kpiPropertyConfig', 'list', tenantId] as const,
  },

  // ── Kassenbuch (Cash Book / Ledger) ─────────────────────────────
  kassenbuch: {
    all: ['kassenbuch'] as const,
    entries: (tenantId: string, period: string) => ['kassenbuch', 'entries', tenantId, period] as const,
    counters: (tenantId: string, counterId: string) => ['kassenbuch', 'counters', tenantId, counterId] as const,
    locks: (tenantId: string, counterId: string) => ['kassenbuch', 'locks', tenantId, counterId] as const,
    settings: (tenantId: string) => ['kassenbuch', 'settings', tenantId] as const,
  },

  // ── Buchhaltung (Accounting) ────────────────────────────────────
  // settings stays (webhook-apaleo-v2 reads it server-side)
  buchhaltung: {
    all: ['buchhaltung'] as const,
    docs: (tenantId: string) => ['buchhaltung', 'docs', tenantId] as const,
    settings: (tenantId: string) => ['buchhaltung', 'settings', tenantId] as const,
  },

  // ── Inventory ───────────────────────────────────────────────────
  inventory: {
    all: ['inventory'] as const,
    items: (tenantId: string) => ['inventory', 'items', tenantId] as const,
    history: (tenantId: string) => ['inventory', 'history', tenantId] as const,
  },

  // ── Haustechnik (Building Maintenance) ──────────────────────────
  haustechnik: {
    all: ['haustechnik'] as const,
    lists: (tenantId: string) => ['haustechnik', 'lists', tenantId] as const,
    listItems: (listId: string) => ['haustechnik', 'listItems', listId] as const,
    devices: (tenantId: string) => ['haustechnik', 'devices', tenantId] as const,
    // Optional extra segments (configId/rowPrefix) only appear for the
    // Internal Resources duplicated checks — the original Zimmercheck key
    // stays byte-identical, and the prefix-only realtime invalidations
    // (['haustechnik','config'] / ['haustechnik','roomChecks']) still match
    // the extended keys because invalidateQueries matches by prefix.
    config: (tenantId: string, configId?: string) => (configId
      ? ['haustechnik', 'config', tenantId, configId] as const
      : ['haustechnik', 'config', tenantId] as const),
    roomChecks: (tenantId: string, rowPrefix?: string) => (rowPrefix
      ? ['haustechnik', 'roomChecks', tenantId, rowPrefix] as const
      : ['haustechnik', 'roomChecks', tenantId] as const),
  },

  // ── Sperrapartments (blocked apartments / OOO snapshots) ────────
  // Snapshot mirror of Apaleo maintenance windows. The keyword allowlist is
  // deliberately NOT keyed here — it lives under haustechnik.config(…,
  // 'sperrapartments') so the existing ht_config realtime handler (which
  // invalidates ['haustechnik','config']) refreshes it for all users.
  // sperrapartment_maintenances events invalidate ['sperrapartment'] via
  // the realtimeStore handler added in the same commit.
  sperrapartment: {
    all: ['sperrapartment'] as const,
    // keywordsKey (joined keyword list) is a key segment so changing the
    // allowlist refetches the server-side filtered snapshot query.
    snapshots: (tenantId: string, keywordsKey?: string) => (keywordsKey
      ? ['sperrapartment', 'snapshots', tenantId, keywordsKey] as const
      : ['sperrapartment', 'snapshots', tenantId] as const),
  },

  // ── Housekeeping ────────────────────────────────────────────────
  housekeeping: {
    all: ['housekeeping'] as const,
    rooms: (tenantId: string) => ['housekeeping', 'rooms', tenantId] as const,
    settings: (tenantId: string) => ['housekeeping', 'settings', tenantId] as const,
    // Priority/override flags for the dashboard rooms widget. Deliberately
    // keyed under 'rooms' (not its own segment) so the realtimeStore
    // housekeeping_rooms handler — which invalidates ['housekeeping','rooms']
    // — refreshes it. The previous ['housekeeping','rooms_overrides',...] key
    // diverged at segment 2 and never refreshed live (audit 2026-10-13).
    roomsOverrides: (tenantId: string) => ['housekeeping', 'rooms', 'overrides', tenantId] as const,
  },

  // ── Fundsachen (Lost & Found) ───────────────────────────────────
  fundsachen: {
    all: ['fundsachen'] as const,
    list: (tenantId: string) => ['fundsachen', 'list', tenantId] as const,
    activities: (tenantId: string) => ['fundsachen', 'activities', tenantId] as const,
  },

  // ── Waiting List ────────────────────────────────────────────────
  waitingList: {
    all: ['waitingList'] as const,
    list: (tenantId: string) => ['waitingList', 'list', tenantId] as const,
  },

  // ── Overbooking Locks ───────────────────────────────────────────
  overbookingLocks: {
    all: ['overbookingLocks'] as const,
    list: (tenantId: string) => ['overbookingLocks', 'list', tenantId] as const,
  },

  // ── Automations ─────────────────────────────────────────────────
  automations: {
    all: ['automations'] as const,
    configs: (tenantId: string) => ['automations', 'configs', tenantId] as const,
    // No-Code Automation Builder rules (automation_rules via api/automation-rules.js)
    rules: (tenantId: string) => ['automations', 'rules', tenantId] as const,
    ruleRuns: (tenantId: string, ruleId: string) => ['automations', 'ruleRuns', tenantId, ruleId] as const,
  },

  // ── Internal Messenger (standalone DMs + groups) ────────────────
  // chatConversations/chatMessages are the messenger tables (20260915110000).
  // The domain chat panels use useRealtimeChat's ['chat','messages',...] keys —
  // deliberately different prefix so the two never cross-invalidate.
  messenger: {
    all: ['messenger'] as const,
    conversations: (tenantId: string) => ['messenger', 'conversations', tenantId] as const,
    messages: (tenantId: string, conversationId: string) =>
      ['messenger', 'messages', tenantId, conversationId] as const,
    // Reaction rows per conversation (phase-2 P3 — realtime broadcast from
    // broadcast_chat_reaction_changes feeds this slice; plan §Scope).
    reactions: (tenantId: string, conversationId: string) =>
      ['messenger', 'reactions', tenantId, conversationId] as const,
    // Global ("for everyone") pinned messages per conversation. Realtime via
    // broadcast topic :pins (migration 20261012110000) — the pins hook owns
    // the dedicated channel, same carve-out as reactions.
    pins: (tenantId: string, conversationId: string) =>
      ['messenger', 'pins', tenantId, conversationId] as const,
    // Full-history message search via the chat_search_messages RPC
    // (migration 20261015120000). Term is part of the key — each search term
    // gets its own cache slice (same pattern as queryKeys.search.global).
    messageSearch: (tenantId: string, conversationId: string, term: string) =>
      ['messenger', 'messageSearch', tenantId, conversationId, term] as const,
  },

  // ── Chat / Support Tickets ──────────────────────────────────────
  supportTickets: {
    all: ['supportTickets'] as const,
    list: (tenantId: string) => ['supportTickets', 'list', tenantId] as const,
    detail: (ticketId: string) => ['supportTickets', 'detail', ticketId] as const,
    replies: (ticketId: string) => ['supportTickets', 'replies', ticketId] as const,
    creatorDepartments: (tenantId: string) => ['supportTickets', 'creatorDepartments', tenantId] as const,
    // Admin-only cross-tenant widget feed (OpenTicketsWidget). Lives under the
    // ['supportTickets'] prefix so the realtimeStore support_tickets handler
    // invalidates it — the previous ad-hoc ['admin','supportTickets','widget']
    // key sat under the unrelated 'admin' root and never refreshed live
    // (audit 2026-10-13, same class as the Team-on-Duty bug).
    adminWidget: () => ['supportTickets', 'adminWidget'] as const,
  },

  // ── Notifications ───────────────────────────────────────────────
  notifications: {
    all: ['notifications'] as const,
    list: (tenantId: string) => ['notifications', 'list', tenantId] as const,
    unread: (tenantId: string) => ['notifications', 'unread', tenantId] as const,
  },

  // ── Anleitungen (Guides/SOPs) ───────────────────────────────────
  anleitungen: {
    all: ['anleitungen'] as const,
    list: (tenantId: string) => ['anleitungen', 'list', tenantId] as const,
    detail: (guideId: string) => ['anleitungen', 'detail', guideId] as const,
  },

  // ── Loyalty ─────────────────────────────────────────────────────
  loyalty: {
    all: ['loyalty'] as const,
    transactions: (guestId: string) => ['loyalty', 'transactions', guestId] as const,
  },

  // ── Audit Log ───────────────────────────────────────────────────
  auditLog: {
    all: ['auditLog'] as const,
    list: (tenantId: string) => ['auditLog', 'list', tenantId] as const,
  },

  // ── Global Logs / Tasks ─────────────────────────────────────────
  globalLogs: {
    all: ['globalLogs'] as const,
    list: (tenantId: string) => ['globalLogs', 'list', tenantId] as const,
  },
  globalTasks: {
    all: ['globalTasks'] as const,
    list: (tenantId: string) => ['globalTasks', 'list', tenantId] as const,
  },

  // ── Config tables (relaxed RLS — mig 20260807000004) ────────────
  appConfig: {
    all: ['appConfig'] as const,
    list: () => ['appConfig', 'list'] as const,
  },
  systemConfig: {
    all: ['systemConfig'] as const,
    list: () => ['systemConfig', 'list'] as const,
  },
  adminConfig: {
    all: ['adminConfig'] as const,
    list: () => ['adminConfig', 'list'] as const,
  },
};
